import type {
  Interaction,
  InteractionDetail,
  InteractionList,
  InteractionListQuery,
} from '@inbound/contract';

import { apiSlice } from './api-slice.js';

/**
 * Interactions and transcripts (S08).
 *
 * Read-only, and no polling: an interaction is a conclusion about a call that
 * has already ended, so nothing about a row changes once it exists. The list
 * gains rows as calls are analysed, which the `Interaction` tag picks up when
 * the analyzer's webhook lands.
 */
export const interactionsApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getInteractions: build.query<InteractionList, InteractionListQuery>({
      query: (params) => ({ url: '/interactions', params }),
      providesTags: ['Interaction'],
    }),

    /**
     * One interaction with its transcript.
     *
     * Fetched on demand rather than carried in the list: a transcript is dozens
     * of turns, and a page of ten would move a great deal of text for the one
     * row an operator opens.
     */
    getInteraction: build.query<InteractionDetail, string>({
      query: (id) => ({ url: `/interactions/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Interaction' as const, id }],
    }),
  }),
});

export const { useGetInteractionsQuery, useGetInteractionQuery } = interactionsApi;

/** Re-exported so a component need not import from two places. */
export type { Interaction, InteractionDetail };
